import {createPortError,isPortError} from '../platform/port-error.js';
import {feedbackSubmissionToRemoteRow} from './model.js';

export const FEEDBACK_SUBMISSIONS_TABLE='feedback_submissions';

function requireClient(client){
  if(typeof client?.from!=='function')throw new TypeError('Supabase feedback client is required');
  return client;
}

function abortFailure({externalSignal,timedOut}){
  if(externalSignal?.aborted&&!timedOut){
    return createPortError('FEEDBACK_SUBMIT_CANCELLED',{cancelled:true});
  }
  return createPortError('FEEDBACK_SUBMIT_TIMEOUT',{retryable:true,timeout:true});
}

function providerFailure(error){
  const code=String(error?.code||'').toUpperCase();
  if(code==='23505')return null;
  if(code==='42501'||code==='23514'||code==='22P02'){
    return createPortError('FEEDBACK_SUBMIT_REJECTED',{retryable:false});
  }
  return createPortError('FEEDBACK_SUBMIT_UNAVAILABLE',{retryable:true});
}

export function createSupabaseFeedbackSubmissionRemote({
  client,
  timeoutMs=8000,
  setTimer=globalThis.setTimeout,
  clearTimer=globalThis.clearTimeout
}={}){
  const supabase=requireClient(client);
  const timeout=Math.max(250,Math.min(30_000,Number(timeoutMs)||8000));

  async function submit(submission,{signal}={}){
    if(signal?.aborted)throw createPortError('FEEDBACK_SUBMIT_CANCELLED',{cancelled:true});
    const controller=new AbortController();
    let timedOut=false;
    const forwardAbort=()=>controller.abort();
    signal?.addEventListener?.('abort',forwardAbort,{once:true});
    const timer=setTimer?.(()=>{
      timedOut=true;
      controller.abort();
    },timeout);

    try{
      let query=supabase
        .from(FEEDBACK_SUBMISSIONS_TABLE)
        .insert(feedbackSubmissionToRemoteRow(submission));
      if(typeof query?.abortSignal==='function')query=query.abortSignal(controller.signal);
      const result=await query;
      const error=result?.error??null;
      if(!error)return Object.freeze({ok:true,status:'submitted',duplicate:false});
      if(String(error.code||'').toUpperCase()==='23505'){
        return Object.freeze({ok:true,status:'duplicate',duplicate:true});
      }
      throw providerFailure(error);
    }catch(error){
      if(isPortError(error))throw error;
      if(controller.signal.aborted)throw abortFailure({externalSignal:signal,timedOut});
      throw createPortError('FEEDBACK_SUBMIT_UNAVAILABLE',{retryable:true});
    }finally{
      if(timer!=null)clearTimer?.(timer);
      signal?.removeEventListener?.('abort',forwardAbort);
    }
  }

  return Object.freeze({submit});
}
